import React, { useState, useEffect, useCallback } from 'react';
import {
  getBorge,
  createBorg,
  updateBorg,
  deleteBorg,
  getLandingBoodskap,
  setLandingBoodskap
} from '../services/borg_services';

// Admin management of the beoordelaars' landing page: the sponsors (one hoofborg plus the
// rest) and the thank-you message. Both change every year, so the admin edits them here.

const cardStyle = {
  marginBottom: '1.5rem',
  padding: '1rem',
  border: '1px solid #ddd',
  borderRadius: '8px',
  backgroundColor: '#f9f9f9'
};

const labelStyle = { display: 'block', marginBottom: '0.35rem', fontWeight: 'bold', color: '#0e1e3b' };

const inputStyle = {
  width: '100%',
  padding: '0.5rem',
  border: '1px solid #ccc',
  borderRadius: '4px',
  fontSize: '1rem',
  boxSizing: 'border-box'
};

const buttonStyle = (backgroundColor, disabled = false) => ({
  padding: '0.5rem 1rem',
  backgroundColor,
  color: 'white',
  border: 'none',
  borderRadius: '4px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
  fontSize: '0.95rem',
  whiteSpace: 'nowrap'
});

const LEEG = {
  naam: '',
  logo: '',
  bio: '',
  webwerf: '',
  facebook: '',
  instagram: '',
  x: '',
  linkedin: '',
  epos: '',
  hoofborg: false,
  volgorde: 0
};

// The link fields must be full web addresses (starting with https://). Without the https:// a
// browser treats "www.borg.co.za" as a page on this site, so the link wouldn't reach the sponsor.
const SKAKEL_VELDE = {
  logo: { etiket: 'Logo', voorbeeld: 'https://www.borg.co.za/logo.png' },
  webwerf: { etiket: 'Webwerf', voorbeeld: 'https://www.borg.co.za' },
  facebook: { etiket: 'Facebook', voorbeeld: 'https://www.facebook.com/borg' },
  instagram: { etiket: 'Instagram', voorbeeld: 'https://www.instagram.com/borg' },
  x: { etiket: 'X (Twitter)', voorbeeld: 'https://x.com/borg' },
  linkedin: { etiket: 'LinkedIn', voorbeeld: 'https://www.linkedin.com/company/borg' }
};
const EPOS_PATROON = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isVolleWebadres = (waarde) => {
  if (/\s/.test(waarde)) return false;
  try {
    const url = new URL(waarde);
    return (url.protocol === 'https:' || url.protocol === 'http:') && url.hostname.includes('.');
  } catch {
    return false;
  }
};

// Field -> message for every filled-in field that isn't in the right format
const kontroleerBorg = (form) => {
  const foute = {};
  Object.entries(SKAKEL_VELDE).forEach(([sleutel, { voorbeeld }]) => {
    const waarde = form[sleutel].trim();
    if (waarde && !isVolleWebadres(waarde)) {
      foute[sleutel] = `Gebruik die volle webadres met https://, bv. ${voorbeeld}`;
    }
  });
  const epos = form.epos.trim();
  if (epos && !EPOS_PATROON.test(epos)) {
    foute.epos = "Gebruik 'n geldige e-posadres, bv. info@borg.co.za";
  }
  return foute;
};

function Borge() {
  const [borge, setBorge] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [editingId, setEditingId] = useState(null); // borg_id being edited, 'nuut' for the form
  const [form, setForm] = useState(LEEG);
  const [veldFoute, setVeldFoute] = useState({}); // field -> format message
  const [boodskap, setBoodskap] = useState('');
  const [boodskapBusy, setBoodskapBusy] = useState(false);
  const [boodskapStoor, setBoodskapStoor] = useState('');

  const laai = useCallback(async () => {
    try {
      setBorge(await getBorge());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    laai();
    getLandingBoodskap()
      .then(data => setBoodskap(data.boodskap || ''))
      .catch(() => {});
  }, [laai]);

  const stel = (velde) => {
    setForm((huidig) => ({ ...huidig, ...velde }));
    // A field that changes loses its message until the next save
    setVeldFoute((huidig) => {
      const nuut = { ...huidig };
      Object.keys(velde).forEach(sleutel => { delete nuut[sleutel]; });
      return nuut;
    });
  };

  const beginNuwe = () => {
    setEditingId('nuut');
    setForm({ ...LEEG, volgorde: borge.length });
    setVeldFoute({});
    setError('');
  };

  const beginWysig = (b) => {
    const vorm = {
      naam: b.naam || '',
      logo: b.logo || '',
      bio: b.bio || '',
      webwerf: b.webwerf || '',
      facebook: b.facebook || '',
      instagram: b.instagram || '',
      x: b.x || '',
      linkedin: b.linkedin || '',
      epos: b.epos || '',
      hoofborg: !!b.hoofborg,
      volgorde: b.volgorde || 0
    };
    setEditingId(b.borg_id);
    setForm(vorm);
    // Show straight away which saved links aren't full web addresses
    setVeldFoute(kontroleerBorg(vorm));
    setError('');
  };

  const stoor = async (e) => {
    e.preventDefault();
    if (!form.naam.trim()) {
      setError('Naam is verpligtend.');
      return;
    }
    const foute = kontroleerBorg(form);
    setVeldFoute(foute);
    if (Object.keys(foute).length > 0) {
      setError('Maak eers die gemerkte velde reg.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      if (editingId === 'nuut') {
        await createBorg(form);
      } else {
        await updateBorg(editingId, form);
      }
      setEditingId(null);
      setForm(LEEG);
      await laai();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const verwyder = async (b) => {
    if (!window.confirm(`Verwyder borg '${b.naam}'?`)) return;
    setBusy(true);
    setError('');
    try {
      await deleteBorg(b.borg_id);
      if (editingId === b.borg_id) { setEditingId(null); setForm(LEEG); }
      await laai();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const stoorBoodskap = async () => {
    setBoodskapBusy(true);
    setError('');
    setBoodskapStoor('');
    try {
      await setLandingBoodskap(boodskap);
      setBoodskapStoor('Die boodskap is gestoor.');
    } catch (err) {
      setError(err.message);
    } finally {
      setBoodskapBusy(false);
    }
  };

  const veld = (sleutel, etiket, { tipe = 'text', voorbeeld = etiket, wenk = null } = {}) => (
    <div style={{ marginBottom: '0.75rem' }}>
      <label style={labelStyle} htmlFor={`borg-${sleutel}`}>{etiket}</label>
      {wenk && <div style={{ color: '#666', fontSize: '0.85rem', margin: '-0.15rem 0 0.35rem 0' }}>{wenk}</div>}
      <input
        id={`borg-${sleutel}`}
        type={tipe}
        value={form[sleutel]}
        onChange={(e) => stel({ [sleutel]: e.target.value })}
        placeholder={voorbeeld}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        aria-invalid={!!veldFoute[sleutel]}
        style={{ ...inputStyle, ...(veldFoute[sleutel] ? { borderColor: '#a32020', backgroundColor: '#fff5f5' } : {}) }}
      />
      {veldFoute[sleutel] && (
        <div style={{ color: '#a32020', fontSize: '0.85rem', marginTop: '0.25rem' }}>{veldFoute[sleutel]}</div>
      )}
    </div>
  );

  // A link field: a full web address, with an example of the format
  const skakelVeld = (sleutel) => veld(sleutel, SKAKEL_VELDE[sleutel].etiket, {
    tipe: 'url',
    voorbeeld: SKAKEL_VELDE[sleutel].voorbeeld,
    wenk: 'Die volle webadres, wat met https:// begin'
  });

  return (
    <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto' }}>
      <h2 style={{ color: '#0e1e3b' }}>Borge</h2>
      <p style={{ color: '#666', marginBottom: '2rem' }}>
        Bestuur die borge wat die beoordelaars op hul tuisblad sien. Daar is een hoofborg en
        soveel addisionele borge as jy wil.{' '}
        <a href="/tuis" style={{ color: '#77502d', fontWeight: 'bold' }}>Sien die tuisblad</a>{' '}
        soos die beoordelaars dit sien.
      </p>

      {error && (
        <div style={{
          padding: '1rem', backgroundColor: '#f8d7da', color: '#721c24',
          border: '1px solid #f5c6cb', borderRadius: '4px', marginBottom: '1rem'
        }}>
          Fout: {error}
        </div>
      )}

      {/* The thank-you message at the top of the landing page */}
      <div style={cardStyle}>
        <h3 style={{ marginTop: 0, color: '#0e1e3b' }}>Boodskap vir die beoordelaars</h3>
        <p style={{ color: '#666', marginTop: 0 }}>
          'n Paar woorde van dank aan die beoordelaars wat die dag bywoon. Dit wys heel bo op hul tuisblad.
        </p>
        <textarea
          value={boodskap}
          onChange={(e) => setBoodskap(e.target.value)}
          rows={4}
          placeholder="Bv. Baie dankie dat julle hierdie jaar se projekte kom beoordeel het!"
          style={{ ...inputStyle, fontFamily: 'inherit' }}
        />
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginTop: '0.75rem' }}>
          <button type="button" onClick={stoorBoodskap} disabled={boodskapBusy} style={buttonStyle('#0e1e3b', boodskapBusy)}>
            {boodskapBusy ? 'Stoor...' : 'Stoor boodskap'}
          </button>
          {boodskapStoor && <span style={{ color: '#1e6b35' }}>{boodskapStoor}</span>}
        </div>
      </div>

      {/* Add / edit a borg */}
      {editingId === null ? (
        <button type="button" onClick={beginNuwe} style={buttonStyle('#77502d')}>
          + Voeg borg by
        </button>
      ) : (
        <form onSubmit={stoor} noValidate style={{ ...cardStyle, backgroundColor: 'white' }}>
          <h3 style={{ marginTop: 0, color: '#0e1e3b' }}>
            {editingId === 'nuut' ? 'Nuwe borg' : 'Wysig borg'}
          </h3>
          {veld('naam', 'Naam *')}
          {skakelVeld('logo')}
          <div style={{ marginBottom: '0.75rem' }}>
            <label style={labelStyle} htmlFor="borg-bio">Bio</label>
            <textarea
              id="borg-bio"
              value={form.bio}
              onChange={(e) => stel({ bio: e.target.value })}
              rows={3}
              placeholder="'n Kort beskrywing van die borg"
              style={{ ...inputStyle, fontFamily: 'inherit' }}
            />
          </div>
          {skakelVeld('webwerf')}
          {skakelVeld('facebook')}
          {skakelVeld('instagram')}
          {skakelVeld('x')}
          {skakelVeld('linkedin')}
          {veld('epos', 'Kontak e-pos', { tipe: 'email', voorbeeld: 'info@borg.co.za' })}
          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', marginBottom: '0.75rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'bold', color: '#0e1e3b' }}>
              <input
                type="checkbox"
                checked={form.hoofborg}
                onChange={(e) => stel({ hoofborg: e.target.checked })}
              />
              Hoofborg
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'bold', color: '#0e1e3b' }}>
              Volgorde
              <input
                type="number"
                value={form.volgorde}
                onChange={(e) => stel({ volgorde: parseInt(e.target.value, 10) || 0 })}
                style={{ ...inputStyle, width: '80px' }}
              />
            </label>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button type="submit" disabled={busy} style={buttonStyle('#0e1e3b', busy)}>
              {busy ? 'Stoor...' : 'Stoor borg'}
            </button>
            <button type="button" onClick={() => { setEditingId(null); setForm(LEEG); }} style={buttonStyle('#999')}>
              Kanselleer
            </button>
          </div>
        </form>
      )}

      {/* Existing borge */}
      <h3 style={{ color: '#0e1e3b', marginTop: '1.5rem' }}>Bestaande borge ({borge.length})</h3>
      {loading ? (
        <p>Laai borge...</p>
      ) : borge.length === 0 ? (
        <p style={{ color: '#666', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
          Nog geen borge nie. Voeg die eerste een hierbo by.
        </p>
      ) : (
        <div style={{ display: 'grid', gap: '0.5rem' }}>
          {borge.map(b => (
            <div
              key={b.borg_id}
              style={{
                padding: '1rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: 'white',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', textAlign: 'left' }}>
                {b.logo
                  ? <img src={b.logo} alt={b.naam} style={{ width: '56px', height: '56px', objectFit: 'contain', borderRadius: '6px', backgroundColor: '#f4f4f4' }} />
                  : <div style={{ width: '56px', height: '56px', borderRadius: '6px', backgroundColor: '#eee' }} />}
                <div>
                  <strong style={{ color: '#0e1e3b', fontSize: '1.1rem' }}>
                    {b.naam}{b.hoofborg ? ' (hoofborg)' : ''}
                  </strong>
                  {b.bio && <div style={{ fontSize: '0.9rem', color: '#666' }}>{b.bio}</div>}
                  <div style={{ fontSize: '0.8rem', color: '#888' }}>
                    {[b.webwerf, b.facebook && 'Facebook', b.instagram && 'Instagram', b.x && 'X', b.linkedin && 'LinkedIn', b.epos]
                      .filter(Boolean).join(' · ')}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button type="button" onClick={() => beginWysig(b)} disabled={busy} style={buttonStyle('#0e1e3b', busy)}>
                  Wysig
                </button>
                <button type="button" onClick={() => verwyder(b)} disabled={busy} style={buttonStyle('#77502d', busy)}>
                  Verwyder
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Borge;
